import React, { useState, useRef } from "react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { Column, Row, Text, Icon } from "@amzn/meridian";
import Textarea from "@amzn/meridian/textarea";
import Checkbox from "@amzn/meridian/checkbox";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import cameraTokens from "@amzn/meridian-tokens/base/icon/camera";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-large";
import { InspectionResult } from "src/services/inspectionService";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { overrideInspectionResult, rememberReason } from "src/store/inspectionResultsSlice";
import useKeyboardScrollReset from "src/hooks/useKeyboardScrollReset";
import ManualCaptureMultipleStep from "../steps/ManualCaptureMultipleStep";
import ReasonTag from "./ReasonTag";
import {
    CANNED_REASON_AI_ERROR,
    CANNED_REASON_WRONG_IMAGE,
    pickReuseReason,
} from "./reasonTags";
import { EvidencePhoto } from "./evidence";
import "./overrideCompliancePage.scss";

type SelectedTag = "aiError" | "wrongImage" | "reuse" | null;

const OverrideCompliancePage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
    const [comment, setComment] = useState<string>("");
    const [commentTouched, setCommentTouched] = useState(false);
    const [evidence, setEvidence] = useState<EvidencePhoto[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    // AI-mode-only state; unused in non-AI modes.
    const [useOriginalPhoto, setUseOriginalPhoto] = useState(false);
    const [selectedTag, setSelectedTag] = useState<SelectedTag>(null);

    useKeyboardScrollReset();

    const suggestedResolutionReason = useAppSelector((rootState) => rootState.inspectionResults.suggestedResolutionReason);

    const state = location.state as {
        result: InspectionResult;
        questionText: string;
        objectCheck?: { object_name: string; is_compliant: boolean; ai_reason: string };
        questionMode?: string;
    } | null;

    if (!state) {
        return <Navigate to={`/configurationDrivenInspections/${inspectionId}/review`} replace />;
    }

    const { result, questionText, objectCheck } = state;

    // AI mode unlocks the reason-tag/original-photo enhancements; other modes render as before.
    const isAiMode = state.questionMode === "ai";
    const requiresEvidence = state.questionMode !== "manual";

    const reuseReason = pickReuseReason(suggestedResolutionReason);

    const getGuidanceMessage = (): string => {
        if (objectCheck) {
            const displayName = objectCheck.object_name.replace(/\s*sign\s*$/i, "").trim();
            return `Take multiple photos to cover ${displayName} signage at the cage entrance`;
        }
        return "Take photos as evidence for the override";
    };

    const addEvidenceFiles = (files: File[]) => {
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                setEvidence((prev) => [...prev, { file, preview: event.target?.result as string }]);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleRemoveEvidence = (index: number) => {
        setEvidence((prev) => prev.filter((_, i) => i !== index));
    };

    const handleCameraSubmit = (files: File[]) => {
        addEvidenceFiles(files);
        setShowCamera(false);
    };

    const handleAttach = () => {
        if (fileInputRef.current) {
            fileInputRef.current.removeAttribute("capture");
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        addEvidenceFiles(files);

        e.target.value = "";
    };

    const trimmedComment = comment.trim();
    const hasReason = trimmedComment !== "";

    const applyTagTap = (tagType: Exclude<SelectedTag, null>, tagText: string): boolean => {
        if (selectedTag === tagType) {
            setSelectedTag(null);
            return false;
        }
        setSelectedTag(tagType);
        setComment(tagText);
        setCommentTouched(true);
        setSubmitError(null);
        return true;
    };

    const handleAiErrorTagTap = (tagText: string) => {
        if (applyTagTap("aiError", tagText)) {
            setUseOriginalPhoto(true);
            setEvidence([]);
        } else {
            setUseOriginalPhoto(false);
        }
    };

    const handleWrongImageTagTap = (tagText: string) => {
        if (applyTagTap("wrongImage", tagText)) {
            setUseOriginalPhoto(false);
        }
    };

    const handleReuseReasonTagTap = (tagText: string) => {
        applyTagTap("reuse", tagText);
    };

    const canSubmit = isAiMode
        ? selectedAnswer !== null &&
          hasReason &&
          (useOriginalPhoto || evidence.length > 0)
        : selectedAnswer !== null &&
          hasReason &&
          (!requiresEvidence || evidence.length > 0);

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        setSubmitError(null);

        const newIsCompliant = selectedAnswer === "Yes";
        const newConfirmedResult = newIsCompliant ? "YES" : "NO";

        const formData = new FormData();
        formData.append("result_id", result.resultId);
        formData.append("ai_result", result.aiResult || "");
        formData.append("confirmed_result", newConfirmedResult);
        formData.append("is_compliant", String(newIsCompliant));
        formData.append("override_reason", trimmedComment);

        if (objectCheck) {
            formData.append("object_name", objectCheck.object_name);
        }

        if (isAiMode) {
            formData.append("use_original_photo", String(useOriginalPhoto));
        }

        if (!useOriginalPhoto) {
            evidence.forEach(({ file }) => {
                formData.append("files", file);
            });
        }

        try {
            await dispatch(overrideInspectionResult({
                inspection_id: inspectionId!,
                question_id: result.questionId,
                formData,
            })).unwrap();

            dispatch(rememberReason(trimmedComment));

            navigate(-1);
        } catch (error) {
            console.error("Failed to submit override:", error);
            const message =
                typeof error === "string"
                    ? error
                    : error instanceof Error
                        ? error.message
                        : "Failed to submit override. Please try again.";
            setSubmitError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (showCamera) {
        return (
            <ManualCaptureMultipleStep
                humanText={getGuidanceMessage()}
                activeQuestionIds={[]}
                onComplete={() => setShowCamera(false)}
                onSubmitFiles={handleCameraSubmit}
            />
        );
    }

    const reasonField = (
        <Column spacing="100">
            <Text type="h100" color="primary">Reason (Required)</Text>
            <Textarea
                value={comment}
                onChange={(value: string) => {
                    setComment(value);
                    setCommentTouched(true);
                    setSubmitError(null);
                }}
                onBlur={() => setCommentTouched(true)}
                placeholder="Enter a reason for this override..."
                rows={2}
                error={commentTouched && !hasReason}
                errorMessage={
                    commentTouched && !hasReason
                        ? "A reason is required to override a result."
                        : undefined
                }
            />
        </Column>
    );

    const reasonTagsRow = (
        <Row spacing="300" wrap="down">
            <ReasonTag
                text={CANNED_REASON_AI_ERROR}
                variant="outline"
                selected={selectedTag === "aiError"}
                onTap={handleAiErrorTagTap}
            />
            <ReasonTag
                text={CANNED_REASON_WRONG_IMAGE}
                variant="outline"
                selected={selectedTag === "wrongImage"}
                onTap={handleWrongImageTagTap}
            />
            {reuseReason && (
                <ReasonTag
                    text={reuseReason}
                    variant="dashed"
                    selected={selectedTag === "reuse"}
                    onTap={handleReuseReasonTagTap}
                />
            )}
        </Row>
    );

    const evidenceSection = (
        <>
            <Text type="h100" color="primary">Add evidence</Text>
            <Row spacing="300" alignmentHorizontal="center">
                <div
                    className={`override-compliance-page__evidence-box ${isAiMode && useOriginalPhoto ? "override-compliance-page__evidence-box--disabled" : ""}`}
                    onClick={() => {
                        if (isAiMode && useOriginalPhoto) return;
                        setShowCamera(true);
                    }}
                    role="button"
                >
                    <Icon tokens={cameraTokens} color="success" />
                    <Text type="b100" color="secondary">Capture</Text>
                </div>
                <Text type="b100" color="secondary">OR</Text>
                <div
                    className={`override-compliance-page__evidence-box ${isAiMode && useOriginalPhoto ? "override-compliance-page__evidence-box--disabled" : ""}`}
                    onClick={() => {
                        if (isAiMode && useOriginalPhoto) return;
                        handleAttach();
                    }}
                    role="button"
                >
                    <Icon tokens={uploadTokens} color="success" />
                    <Text type="b100" color="secondary">Attach</Text>
                </div>
            </Row>

            {evidence.length > 0 && (
                <Row spacing="300" wrap="down">
                    {evidence.map(({ preview }, idx) => (
                        <div key={idx} className="override-compliance-page__thumbnail-wrapper">
                            <img
                                src={preview}
                                alt={`Evidence ${idx + 1}`}
                                className="override-compliance-page__thumbnail"
                            />
                            <button
                                type="button"
                                className="override-compliance-page__thumbnail-remove"
                                aria-label={`Remove evidence ${idx + 1}`}
                                onClick={() => handleRemoveEvidence(idx)}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </Row>
            )}
        </>
    );

    return (
        <div className="override-compliance-page pwa-scrollable">
            <Column spacingInset="500" spacing="500">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="override-compliance-page__back"
                        onClick={() => navigate(-1)}
                        role="button"
                    >
                        <Icon tokens={arrowLeftTokens} color="success" />
                    </div>
                    <Text type="b300" color="primary">
                        Override Compliance
                    </Text>
                </Row>

                <p className="override-compliance-page__question-text">{questionText}</p>

                <Row spacing="300">
                    <button
                        className={`override-compliance-page__answer-btn ${selectedAnswer === "Yes" ? "override-compliance-page__answer-btn--selected" : ""}`}
                        onClick={() => setSelectedAnswer("Yes")}
                    >
                        Yes
                    </button>
                    <button
                        className={`override-compliance-page__answer-btn ${selectedAnswer === "No" ? "override-compliance-page__answer-btn--selected" : ""}`}
                        onClick={() => setSelectedAnswer("No")}
                    >
                        No
                    </button>
                </Row>

                {selectedAnswer && isAiMode && (
                    <>
                        {reasonField}
                        {reasonTagsRow}
                        <Checkbox
                            checked={useOriginalPhoto}
                            onChange={setUseOriginalPhoto}
                            disabled={evidence.length > 0}
                        >
                            Use original photo as evidence (No new photo needed)
                        </Checkbox>
                        {evidenceSection}
                    </>
                )}

                {selectedAnswer && !isAiMode && (
                    <>
                        {requiresEvidence && evidenceSection}
                        {reasonField}
                    </>
                )}

                {submitError && (
                    <Text type="b100" color="error">{submitError}</Text>
                )}

            </Column>

            <button
                className={`override-compliance-page__submit-btn ${canSubmit ? "override-compliance-page__submit-btn--enabled" : ""}`}
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
            >
                {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
            </button>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                style={{ display: "none" }}
            />
        </div>
    );
};

export default OverrideCompliancePage;
